/** Strict validators and pinned-version negotiation for protocol v2. */
import { PlatformError, type MatchEndReason } from '@djgg/core';
import {
  canonicalDigest,
  canonicalJson,
  cloneJson,
  validateGameplaySemantic,
  validateJsonObject,
  type JsonValue,
} from '@djgg/gameplay-kit';
import { PROTOCOL_VERSION, type MatchPhase } from './envelope.js';
import { assertValidMatchResultV2, matchOutcomeSummaryV2 } from './resultV2.js';
import {
  MATCH_PLAYER_LIMIT,
  assertValidMatchConfigV1,
  assertValidMatchStateV1,
} from './match.js';
import {
  COMMAND_REJECT_REASONS,
  MATCH_CONNECTION_STATES,
  MATCH_CONNECTION_FAILURE_REASONS_V2,
  PROTOCOL_REJECT_REASONS_V2,
  PROTOCOL_V2,
  PROTOCOL_V2_FEATURES,
  PROTOCOL_V2_LIMITS,
  RESUME_REJECT_REASONS,
  RESYNC_REASONS,
  type ClientHelloV2,
  type ClientActionV2,
  type CommandEnvelopeV2,
  type CommandReceipt,
  type CommandRejectReason,
  type CommandResultV2,
  type EventAudienceV2,
  type EventCursorAckV2,
  type EventRetentionV2,
  type MatchConnectionState,
  type MatchReplayMetadataV1,
  type ProtocolFeatureV2,
  type ProtocolNegotiationResult,
  type ProtocolRejectV2,
  type ResumeRequestV2,
  type ResumeResultV2,
  type ServerHelloV2,
  type SeatNegotiationV2,
  type SnapshotEnvelopeV2,
  type SupportedProtocolVersion,
  type WorldEventV2,
  type UnsequencedWorldEventV2,
} from './v2.js';

const DIGEST = /^[a-f0-9]{64}$/;
const TOKEN = /^[A-Za-z0-9._:-]+$/;
const PHASES: readonly MatchPhase[] = ['lobby', 'starting', 'active', 'paused', 'ended'];
const END_REASONS: readonly MatchEndReason[] = ['score', 'forfeit', 'timeout', 'concede', 'defeat'];
const FEATURE_SET = new Set<string>(PROTOCOL_V2_FEATURES);

function fail(kind: string, message: string): never {
  throw new PlatformError('validation', `invalid ${kind}: ${message}`);
}

function record(value: unknown, path: string): Record<string, unknown> {
  try {
    return validateJsonObject(value, path) as Record<string, unknown>;
  } catch (error) {
    fail(path, error instanceof Error ? error.message : 'must be a JSON object');
  }
}

function exactKeys(value: Record<string, unknown>, required: readonly string[], optional: readonly string[], path: string): void {
  const allowed = new Set([...required, ...optional]);
  for (const key of required) if (!(key in value)) fail(path, `missing ${key}`);
  for (const key of Object.keys(value)) if (!allowed.has(key)) fail(path, `unknown field ${key}`);
}

function int(value: unknown, path: string, min = 0, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isSafeInteger(value) || (value as number) < min || (value as number) > max) {
    fail(path, `must be a safe integer in [${min}, ${max}]`);
  }
  return value as number;
}

function finite(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(path, 'must be a finite number');
  return value;
}

function string(value: unknown, path: string, max: number, pattern?: RegExp): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > max || value.trim() !== value) {
    fail(path, `must be a non-empty trimmed string of at most ${max} characters`);
  }
  if (pattern && !pattern.test(value)) fail(path, 'contains unsupported characters');
  return value;
}

function digest(value: unknown, path: string): string {
  if (typeof value !== 'string' || !DIGEST.test(value)) fail(path, 'must be 64 lowercase hexadecimal characters');
  return value;
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
    Object.freeze(value);
  }
  return value;
}

function sortedUniqueNumbers(value: unknown, path: string, maxValue = MATCH_PLAYER_LIMIT - 1): number[] {
  if (!Array.isArray(value) || value.length > MATCH_PLAYER_LIMIT) fail(path, 'must be a bounded array');
  let previous = -1;
  return value.map((entry, index) => {
    const current = int(entry, `${path}[${index}]`, 0, maxValue);
    if (current <= previous) fail(path, 'must be unique and ascending');
    previous = current;
    return current;
  });
}

function sortedUniqueFeatures(value: unknown, path: string): ProtocolFeatureV2[] {
  if (!Array.isArray(value) || value.length > PROTOCOL_V2_FEATURES.length) fail(path, 'must be a bounded array');
  let previous = '';
  return value.map((entry, index) => {
    if (typeof entry !== 'string' || !FEATURE_SET.has(entry)) fail(`${path}[${index}]`, 'is an unknown feature');
    if (index > 0 && entry <= previous) fail(path, 'must be unique and code-unit sorted');
    previous = entry;
    return entry as ProtocolFeatureV2;
  });
}

function assertV2Header(value: Record<string, unknown>, type: string, path: string): void {
  if (value.v !== PROTOCOL_V2) fail(`${path}.v`, 'must be 2');
  if (value.type !== type) fail(`${path}.type`, `must be ${type}`);
}

export function assertValidClientHelloV2(value: unknown): asserts value is ClientHelloV2 {
  const hello = record(value, 'ClientHelloV2');
  exactKeys(hello, ['v', 'type', 'supportedProtocols', 'supportedFeatures'], [], 'ClientHelloV2');
  assertV2Header(hello, 'clientHello', 'ClientHelloV2');
  if (!Array.isArray(hello.supportedProtocols) || hello.supportedProtocols.length < 1 || hello.supportedProtocols.length > 2) {
    fail('ClientHelloV2.supportedProtocols', 'must contain one or two versions');
  }
  let previous = 0;
  hello.supportedProtocols.forEach((entry, index) => {
    if (entry !== PROTOCOL_VERSION && entry !== PROTOCOL_V2) fail(`ClientHelloV2.supportedProtocols[${index}]`, 'is unsupported');
    if (entry <= previous) fail('ClientHelloV2.supportedProtocols', 'must be unique and ascending');
    previous = entry;
  });
  sortedUniqueFeatures(hello.supportedFeatures, 'ClientHelloV2.supportedFeatures');
}

/** Select only the creation-required version; never select a highest-common version. */
export function negotiateProtocol(
  requirement: unknown,
  clientHello: unknown,
): ProtocolNegotiationResult {
  assertValidClientHelloV2(clientHello);
  const requirementRecord = record(requirement, 'ProtocolRequirement');
  exactKeys(requirementRecord, ['requiredProtocol', 'requiredFeatures'], [], 'ProtocolRequirement');
  if (requirementRecord.requiredProtocol !== PROTOCOL_VERSION && requirementRecord.requiredProtocol !== PROTOCOL_V2) {
    fail('ProtocolRequirement.requiredProtocol', 'is unsupported');
  }
  const requiredProtocol = requirementRecord.requiredProtocol as SupportedProtocolVersion;
  const requiredFeatures = sortedUniqueFeatures(requirementRecord.requiredFeatures, 'ProtocolRequirement.requiredFeatures');
  if (requiredProtocol === PROTOCOL_VERSION && requiredFeatures.length > 0) {
    fail('ProtocolRequirement.requiredFeatures', 'v1 cannot require v2 features');
  }
  if (!clientHello.supportedProtocols.includes(requiredProtocol)) {
    return { ok: false, reason: 'unsupported-protocol' };
  }
  const missing = requiredFeatures.filter((feature) => !clientHello.supportedFeatures.includes(feature));
  if (missing.length > 0) return { ok: false, reason: 'unsupported-feature', missingFeatures: missing };
  return { ok: true, selectedProtocol: requiredProtocol, features: requiredFeatures };
}

export const negotiateRequiredProtocol = negotiateProtocol;

export function assertValidEventRetentionV2(value: unknown, path = 'EventRetentionV2'): asserts value is EventRetentionV2 {
  const retention = record(value, path);
  exactKeys(
    retention,
    [
      'maxEvents', 'maxEventBytes', 'maxEventAgeMs', 'maxCommandResultsPerPlayer',
      'maxCommandResultBytesPerPlayer', 'maxCommandResultAgeMs', 'oldestRetainedEventSeq', 'newestEventSeq',
    ],
    [],
    path,
  );
  const maxEvents = int(retention.maxEvents, `${path}.maxEvents`, 1, 4096);
  int(retention.maxEventBytes, `${path}.maxEventBytes`, 1024, 16 * 1024 * 1024);
  int(retention.maxEventAgeMs, `${path}.maxEventAgeMs`, 1000, 120_000);
  int(retention.maxCommandResultsPerPlayer, `${path}.maxCommandResultsPerPlayer`, 1, 4096);
  int(retention.maxCommandResultBytesPerPlayer, `${path}.maxCommandResultBytesPerPlayer`, 1024, 8 * 1024 * 1024);
  int(retention.maxCommandResultAgeMs, `${path}.maxCommandResultAgeMs`, 1000, 120_000);
  const newest = int(retention.newestEventSeq, `${path}.newestEventSeq`);
  if (retention.oldestRetainedEventSeq !== null) {
    const oldest = int(retention.oldestRetainedEventSeq, `${path}.oldestRetainedEventSeq`, 1);
    if (oldest > newest || newest - oldest >= maxEvents) fail(path, 'event cursor bounds are inconsistent');
  }
}

export function assertValidServerHelloV2(value: unknown): asserts value is ServerHelloV2 {
  const hello = record(value, 'ServerHelloV2');
  exactKeys(
    hello,
    ['v', 'type', 'selectedProtocol', 'features', 'matchId', 'playerId', 'role', 'seat', 'inputEpoch', 'runtimeContractDigest', 'configDigest', 'retention'],
    [],
    'ServerHelloV2',
  );
  assertV2Header(hello, 'serverHello', 'ServerHelloV2');
  if (hello.selectedProtocol !== PROTOCOL_V2) fail('ServerHelloV2.selectedProtocol', 'must be 2');
  sortedUniqueFeatures(hello.features, 'ServerHelloV2.features');
  string(hello.matchId, 'ServerHelloV2.matchId', 128, TOKEN);
  string(hello.playerId, 'ServerHelloV2.playerId', 128, TOKEN);
  if (hello.role === 'player') int(hello.seat, 'ServerHelloV2.seat', 0, MATCH_PLAYER_LIMIT - 1);
  else if (hello.role === 'spectator') {
    if (hello.seat !== null) fail('ServerHelloV2.seat', 'spectators have no seat');
  } else fail('ServerHelloV2.role', 'must be player or spectator');
  int(hello.inputEpoch, 'ServerHelloV2.inputEpoch', 1);
  digest(hello.runtimeContractDigest, 'ServerHelloV2.runtimeContractDigest');
  digest(hello.configDigest, 'ServerHelloV2.configDigest');
  assertValidEventRetentionV2(hello.retention, 'ServerHelloV2.retention');
}

export function assertValidSeatNegotiationV2(value: unknown): asserts value is SeatNegotiationV2 {
  const result = record(value, 'SeatNegotiationV2');
  exactKeys(result, ['v', 'type', 'status', 'matchId', 'playerId'], [], 'SeatNegotiationV2');
  assertV2Header(result, 'seatNegotiation', 'SeatNegotiationV2');
  if (result.status !== 'waiting') fail('SeatNegotiationV2.status', 'must be waiting');
  string(result.matchId, 'SeatNegotiationV2.matchId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
  string(result.playerId, 'SeatNegotiationV2.playerId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
}

export function assertValidProtocolRejectV2(value: unknown): asserts value is ProtocolRejectV2 {
  const rejection = record(value, 'ProtocolRejectV2');
  assertV2Header(rejection, 'protocolReject', 'ProtocolRejectV2');
  const common = ['v', 'type', 'reason'];
  if (rejection.reason === 'unsupported-protocol') {
    exactKeys(rejection, [...common, 'requiredProtocol'], [], 'ProtocolRejectV2');
    if (rejection.requiredProtocol !== PROTOCOL_VERSION && rejection.requiredProtocol !== PROTOCOL_V2) {
      fail('ProtocolRejectV2.requiredProtocol', 'must be a supported protocol');
    }
  } else if (rejection.reason === 'missing-feature') {
    exactKeys(rejection, [...common, 'missingFeatures'], [], 'ProtocolRejectV2');
    if (sortedUniqueFeatures(rejection.missingFeatures, 'ProtocolRejectV2.missingFeatures').length === 0) {
      fail('ProtocolRejectV2.missingFeatures', 'must not be empty');
    }
  } else if (rejection.reason === 'server-capacity') {
    exactKeys(rejection, common, ['retryAfterMs'], 'ProtocolRejectV2');
    if (rejection.retryAfterMs !== undefined) int(rejection.retryAfterMs, 'ProtocolRejectV2.retryAfterMs', 0, PROTOCOL_V2_LIMITS.retryAfterMs);
  } else {
    exactKeys(rejection, common, [], 'ProtocolRejectV2');
    if (!PROTOCOL_REJECT_REASONS_V2.includes(rejection.reason as (typeof PROTOCOL_REJECT_REASONS_V2)[number])) {
      fail('ProtocolRejectV2.reason', 'is unsupported');
    }
  }
  if (utf8ByteLength(canonicalJson(rejection)) > PROTOCOL_V2_LIMITS.protocolRejectBytes) fail('ProtocolRejectV2', 'exceeds direct rejection limit');
}

export function assertValidClientActionV2<TCommand>(value: unknown, path = 'ClientActionV2'): asserts value is ClientActionV2<TCommand> {
  const action = record(value, path);
  if (action.kind === 'game') {
    exactKeys(action, ['kind', 'command'], [], path);
    if (utf8ByteLength(canonicalJson(action.command)) > PROTOCOL_V2_LIMITS.commandBytes) fail(`${path}.command`, 'exceeds 32 KiB canonical JSON');
  } else {
    exactKeys(action, ['kind'], [], path);
    if (!['begin', 'pause', 'resume', 'concede'].includes(action.kind as string)) fail(`${path}.kind`, 'is unsupported');
  }
}

export function assertValidCommandEnvelopeV2<TCommand>(value: unknown): asserts value is CommandEnvelopeV2<TCommand> {
  const envelope = record(value, 'CommandEnvelopeV2');
  exactKeys(envelope, ['v', 'type', 'matchId', 'playerId', 'epoch', 'seq', 'action'], [], 'CommandEnvelopeV2');
  assertV2Header(envelope, 'command', 'CommandEnvelopeV2');
  string(envelope.matchId, 'CommandEnvelopeV2.matchId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
  string(envelope.playerId, 'CommandEnvelopeV2.playerId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
  int(envelope.epoch, 'CommandEnvelopeV2.epoch', 1);
  int(envelope.seq, 'CommandEnvelopeV2.seq', 1);
  assertValidClientActionV2<TCommand>(envelope.action, 'CommandEnvelopeV2.action');
  if (utf8ByteLength(canonicalJson(envelope)) > PROTOCOL_V2_LIMITS.commandEnvelopeBytes) {
    fail('CommandEnvelopeV2', 'exceeds 64 KiB canonical JSON');
  }
}

export type CommandIdentityV2 = Readonly<Pick<CommandEnvelopeV2<unknown>, 'matchId' | 'playerId' | 'epoch' | 'seq'>>;

/** Internal journal representation, never a replacement wire envelope. Exact
 * requests preserve all canonical bytes. A tombstone contains only the safe
 * identity and cannot establish equality with any later submission. */
export interface CanonicalCommandRequestV2 {
  readonly canonicalJson: string;
  readonly byteLength: number;
  readonly exact: boolean;
}

export type ClassifiedCommandV2 =
  | { readonly kind: 'malformed'; readonly reason: 'malformed-message' }
  | {
      readonly kind: 'command';
      readonly identity: CommandIdentityV2;
      readonly request: CanonicalCommandRequestV2;
      readonly envelope: CommandEnvelopeV2<unknown>;
    }
  | {
      readonly kind: 'rejected';
      readonly identity: CommandIdentityV2;
      readonly request: CanonicalCommandRequestV2;
      readonly reason: 'invalid-command' | 'size-limit';
    };

const COMMAND_JSON_INVALID = Symbol('invalid-command-json');
const COMMAND_JSON_SIZE = Symbol('command-json-size');
const classifiedCommandsV2 = new WeakSet<object>();

function classifiedCommand<T extends ClassifiedCommandV2>(value: T): T {
  classifiedCommandsV2.add(value);
  return Object.freeze(value);
}

/** Host journal boundary: structural copies cannot impersonate a classifier
 * result or bypass its byte/depth/identity checks. */
export function isClassifiedCommandV2(value: unknown): value is ClassifiedCommandV2 {
  return value !== null && typeof value === 'object' && classifiedCommandsV2.has(value);
}

/** A single descriptor-only walk both detaches and encodes. In particular, it
 * never reads an attacker object's length, discriminator, toJSON, getters or
 * thrown exception. Reflection traps may throw; callers return fixed codes.
 * Charge UTF-8 chunks before retaining them, so rejected input never causes an
 * unbounded canonical string, clone, diagnostic path or recursive traversal. */
function boundedCommandJson(value: unknown): { value: JsonValue; canonicalJson: string; byteLength: number } {
  const chunks: string[] = [];
  const ancestors = new Set<object>();
  let bytes = 0;
  let nodes = 0;
  function emit(text: string): void {
    const size = utf8ByteLength(text);
    if (bytes + size > PROTOCOL_V2_LIMITS.commandEnvelopeBytes) throw COMMAND_JSON_SIZE;
    bytes += size;
    chunks.push(text);
  }
  function emitString(text: string): void {
    // Even unescaped UTF-16 code units require at least this many JSON bytes.
    if (text.length + 2 > PROTOCOL_V2_LIMITS.commandEnvelopeBytes - bytes) throw COMMAND_JSON_SIZE;
    emit(JSON.stringify(text));
  }
  function walk(input: unknown, depth: number): JsonValue {
    if (++nodes > 50_000 || depth > 32) throw COMMAND_JSON_INVALID;
    if (input === null || typeof input === 'boolean') {
      emit(JSON.stringify(input));
      return input;
    }
    if (typeof input === 'string') { emitString(input); return input; }
    if (typeof input === 'number') {
      if (!Number.isFinite(input) || (Number.isInteger(input) && !Number.isSafeInteger(input))) throw COMMAND_JSON_INVALID;
      const number = Object.is(input, -0) ? 0 : input;
      emit(JSON.stringify(number));
      return number;
    }
    if (typeof input !== 'object') throw COMMAND_JSON_INVALID;
    const array = Array.isArray(input);
    const prototype = Object.getPrototypeOf(input);
    if (array ? prototype !== Array.prototype : prototype !== Object.prototype && prototype !== null) throw COMMAND_JSON_INVALID;
    if (ancestors.has(input)) throw COMMAND_JSON_INVALID;
    const keys = Reflect.ownKeys(input);
    if (keys.length > 50_000) throw COMMAND_JSON_SIZE;
    if (keys.some(key => typeof key !== 'string')) throw COMMAND_JSON_INVALID;
    ancestors.add(input);
    let out: JsonValue;
    if (array) {
      const length = Object.getOwnPropertyDescriptor(input, 'length');
      if (!length || !('value' in length) || !Number.isSafeInteger(length.value) || length.value < 0) throw COMMAND_JSON_INVALID;
      if (length.value > 10_000) throw COMMAND_JSON_SIZE;
      if (keys.length !== length.value + 1) throw COMMAND_JSON_INVALID;
      const entries: JsonValue[] = [];
      emit('[');
      for (let i = 0; i < length.value; i++) {
        const descriptor = Object.getOwnPropertyDescriptor(input, String(i));
        if (!descriptor || !('value' in descriptor) || !descriptor.enumerable) throw COMMAND_JSON_INVALID;
        if (i > 0) emit(',');
        entries.push(walk(descriptor.value, depth + 1));
      }
      emit(']');
      out = entries;
    } else {
      // Defining data properties avoids inherited setters, including __proto__.
      // The schema validator subsequently rejects forbidden JSON property names.
      const entries: Record<string, JsonValue> = Object.create(null);
      emit('{');
      (keys as string[]).sort().forEach((key, index) => {
        const descriptor = Object.getOwnPropertyDescriptor(input, key);
        if (!descriptor || !('value' in descriptor) || !descriptor.enumerable) throw COMMAND_JSON_INVALID;
        if (index > 0) emit(',');
        emitString(key);
        emit(':');
        entries[key] = walk(descriptor.value, depth + 1);
      });
      emit('}');
      out = entries;
    }
    ancestors.delete(input);
    return out;
  }
  const detached = walk(value, 0);
  return { value: deepFreeze(detached), canonicalJson: chunks.join(''), byteLength: bytes };
}

/** Recover the correlation key before looking at action data. No generated
 * validator or game code runs here. A malformed key cannot receive a command
 * result. Bounded invalid actions retain their exact bytes; non-JSON or over-
 * ceiling actions retain only a tombstone and must conflict on every reuse. */
export function classifyCommandV2(value: unknown): ClassifiedCommandV2 {
  let identity: CommandIdentityV2;
  try {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) throw COMMAND_JSON_INVALID;
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) throw COMMAND_JSON_INVALID;
    const header: Record<string, unknown> = Object.create(null);
    for (const field of ['v', 'type', 'matchId', 'playerId', 'epoch', 'seq']) {
      const descriptor = Object.getOwnPropertyDescriptor(value, field);
      if (!descriptor || !('value' in descriptor) || !descriptor.enumerable) throw COMMAND_JSON_INVALID;
      header[field] = descriptor.value;
    }
    assertV2Header(header, 'command', 'CommandEnvelopeV2');
    const matchId = string(header.matchId, 'CommandEnvelopeV2.matchId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
    const playerId = string(header.playerId, 'CommandEnvelopeV2.playerId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
    const epoch = int(header.epoch, 'CommandEnvelopeV2.epoch', 1);
    const seq = int(header.seq, 'CommandEnvelopeV2.seq', 1);
    identity = Object.freeze({ matchId, playerId, epoch, seq });
  } catch {
    return classifiedCommand({ kind: 'malformed', reason: 'malformed-message' });
  }

  let encoded: ReturnType<typeof boundedCommandJson>;
  try {
    encoded = boundedCommandJson(value);
    const detached = encoded.value as Record<string, JsonValue>;
    if (detached.v !== 2 || detached.type !== 'command'
      || detached.matchId !== identity.matchId || detached.playerId !== identity.playerId
      || detached.epoch !== identity.epoch || detached.seq !== identity.seq) throw COMMAND_JSON_INVALID;
  } catch (failure) {
    const tombstone = boundedCommandJson({ v: 2, type: 'command', ...identity });
    return classifiedCommand({
      kind: 'rejected', identity,
      request: Object.freeze({ canonicalJson: tombstone.canonicalJson, byteLength: tombstone.byteLength, exact: false }),
      reason: failure === COMMAND_JSON_SIZE ? 'size-limit' : 'invalid-command',
    });
  }
  const request = Object.freeze({ canonicalJson: encoded.canonicalJson, byteLength: encoded.byteLength, exact: true });
  try {
    // Detect a bounded oversized game payload separately from shape failures.
    const data = encoded.value as Record<string, JsonValue>;
    const action = data.action;
    if (action !== null && typeof action === 'object' && !Array.isArray(action) && action.kind === 'game' && 'command' in action) {
      if (boundedCommandJson(action.command).byteLength > PROTOCOL_V2_LIMITS.commandBytes) {
        return classifiedCommand({ kind: 'rejected', identity, request, reason: 'size-limit' });
      }
    }
    assertValidCommandEnvelopeV2(encoded.value);
    return classifiedCommand({ kind: 'command', identity, request, envelope: encoded.value });
  } catch {
    return classifiedCommand({ kind: 'rejected', identity, request, reason: 'invalid-command' });
  }
}

export function assertValidCommandReceipt(value: unknown): asserts value is CommandReceipt {
  const receipt = record(value, 'CommandReceipt');
  exactKeys(receipt, ['epoch', 'seq', 'disposition'], ['reason'], 'CommandReceipt');
  int(receipt.epoch, 'CommandReceipt.epoch', 1);
  int(receipt.seq, 'CommandReceipt.seq', 1);
  if (!['applied', 'rejected', 'queued'].includes(receipt.disposition as string)) fail('CommandReceipt.disposition', 'is unsupported');
  if (receipt.disposition === 'rejected') {
    if (!COMMAND_REJECT_REASONS.includes(receipt.reason as CommandRejectReason)) fail('CommandReceipt.reason', 'is required and must be supported');
  } else if (receipt.reason !== undefined) {
    fail('CommandReceipt.reason', 'is only valid for rejected receipts');
  }
}

export function assertValidCommandResultV2(value: unknown): asserts value is CommandResultV2 {
  const result = record(value, 'CommandResultV2');
  exactKeys(result, ['v', 'type', 'matchId', 'playerId', 'epoch', 'seq', 'appliedTick', 'status'], ['reason'], 'CommandResultV2');
  assertV2Header(result, 'commandResult', 'CommandResultV2');
  string(result.matchId, 'CommandResultV2.matchId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
  string(result.playerId, 'CommandResultV2.playerId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
  int(result.epoch, 'CommandResultV2.epoch', 1);
  int(result.seq, 'CommandResultV2.seq', 1);
  if (result.status !== 'applied' && result.status !== 'rejected') fail('CommandResultV2.status', 'is unsupported');
  if (result.status === 'applied') {
    int(result.appliedTick, 'CommandResultV2.appliedTick');
    if (result.reason !== undefined) fail('CommandResultV2.reason', 'is only valid for rejected results');
  } else {
    if (result.appliedTick !== null) fail('CommandResultV2.appliedTick', 'must be null when rejected');
    if (!COMMAND_REJECT_REASONS.includes(result.reason as CommandRejectReason)) fail('CommandResultV2.reason', 'is required and must be supported');
  }
}

function assertValidAudience(value: unknown, path: string): asserts value is EventAudienceV2 {
  const audience = record(value, path);
  if (!['public', 'actor', 'target', 'slots', 'teams', 'spectators'].includes(audience.kind as string)) fail(`${path}.kind`, 'is unsupported');
  if (audience.kind === 'slots') {
    exactKeys(audience, ['kind', 'slots'], [], path);
    if (sortedUniqueNumbers(audience.slots, `${path}.slots`).length === 0) fail(`${path}.slots`, 'must not be empty');
  } else if (audience.kind === 'teams') {
    exactKeys(audience, ['kind', 'teams'], [], path);
    if (sortedUniqueNumbers(audience.teams, `${path}.teams`).length === 0) fail(`${path}.teams`, 'must not be empty');
  } else {
    exactKeys(audience, ['kind'], [], path);
  }
}

export function assertValidWorldEventV2(value: unknown, path = 'WorldEventV2'): asserts value is WorldEventV2 {
  const event = record(value, path);
  exactKeys(
    event,
    ['eventSeq', 'tick', 'kind', 'semantic', 'audience'],
    ['actorEntityId', 'targetEntityId', 'actorSlot', 'targetSlot', 'position', 'data'],
    path,
  );
  int(event.eventSeq, `${path}.eventSeq`, 1);
  int(event.tick, `${path}.tick`);
  string(event.kind, `${path}.kind`, 128, TOKEN);
  try {
    validateGameplaySemantic(event.semantic);
  } catch (error) {
    fail(`${path}.semantic`, error instanceof Error ? error.message : 'is unsupported');
  }
  if (event.actorEntityId !== undefined) string(event.actorEntityId, `${path}.actorEntityId`, 128, TOKEN);
  if (event.targetEntityId !== undefined) string(event.targetEntityId, `${path}.targetEntityId`, 128, TOKEN);
  if (event.actorSlot !== undefined) int(event.actorSlot, `${path}.actorSlot`, 0, MATCH_PLAYER_LIMIT - 1);
  if (event.targetSlot !== undefined) int(event.targetSlot, `${path}.targetSlot`, 0, MATCH_PLAYER_LIMIT - 1);
  if (event.position !== undefined) {
    const position = record(event.position, `${path}.position`);
    exactKeys(position, ['x', 'y'], [], `${path}.position`);
    finite(position.x, `${path}.position.x`);
    finite(position.y, `${path}.position.y`);
  }
  assertValidAudience(event.audience, `${path}.audience`);
  if ((event.audience as { kind: string }).kind === 'actor' && event.actorSlot === undefined) fail(`${path}.audience`, 'actor audience requires actorSlot');
  if ((event.audience as { kind: string }).kind === 'target' && event.targetSlot === undefined) fail(`${path}.audience`, 'target audience requires targetSlot');
  if (event.data !== undefined && utf8ByteLength(canonicalJson(event.data)) > PROTOCOL_V2_LIMITS.eventBytes) fail(`${path}.data`, 'exceeds 8 KiB canonical JSON');
  if (utf8ByteLength(canonicalJson(event)) > PROTOCOL_V2_LIMITS.eventBytes) fail(path, 'exceeds 8 KiB canonical JSON');
}

export function assertValidUnsequencedWorldEventV2(value: unknown, path = 'UnsequencedWorldEventV2'): asserts value is UnsequencedWorldEventV2 {
  const event = record(value, path);
  exactKeys(event, ['tick', 'kind', 'semantic', 'audience'], ['actorEntityId', 'targetEntityId', 'actorSlot', 'targetSlot', 'position', 'data'], path);
  // Reserve the largest possible host sequence so a valid encoded event fits
  // the unchanged 8 KiB bound after the host assigns its sequence.
  assertValidWorldEventV2({ ...event, eventSeq: Number.MAX_SAFE_INTEGER }, path);
}

function validatePlayers(value: unknown): number {
  if (!Array.isArray(value) || value.length > MATCH_PLAYER_LIMIT) fail('SnapshotEnvelopeV2.players', 'must be a bounded array');
  value.forEach((item, index) => {
    const player = record(item, `SnapshotEnvelopeV2.players[${index}]`);
    exactKeys(player, ['slot', 'uid', 'nickname', 'isBot'], [], `SnapshotEnvelopeV2.players[${index}]`);
    if (player.slot !== index) fail(`SnapshotEnvelopeV2.players[${index}].slot`, 'must equal its canonical array index');
    string(player.uid, `SnapshotEnvelopeV2.players[${index}].uid`, 128);
    string(player.nickname, `SnapshotEnvelopeV2.players[${index}].nickname`, 64);
    if (typeof player.isBot !== 'boolean') fail(`SnapshotEnvelopeV2.players[${index}].isBot`, 'must be boolean');
  });
  return value.length;
}

function validateResync(value: unknown): void {
  if (value === null) return;
  const marker = record(value, 'SnapshotEnvelopeV2.resync');
  if (marker.kind === 'resume') {
    exactKeys(marker, ['kind'], [], 'SnapshotEnvelopeV2.resync');
  } else if (marker.kind === 'full-resync') {
    exactKeys(marker, ['kind', 'reason'], [], 'SnapshotEnvelopeV2.resync');
    if (!RESYNC_REASONS.includes(marker.reason as (typeof RESYNC_REASONS)[number])) fail('SnapshotEnvelopeV2.resync.reason', 'is unsupported');
  } else {
    fail('SnapshotEnvelopeV2.resync.kind', 'is unsupported');
  }
}

const SNAPSHOT_REQUIRED = [
  'v', 'gameId', 'matchId', 'revision', 'tick', 'phase', 'players', 'scores',
  'winnerSlot', 'endReason', 'pausedBySlot', 'countdownMs', 'events', 'eventCursor',
  'configDigest', 'lastCommandResultSeq', 'resync', 'payload',
] as const;

/** A snapshot has independent event/payload byte budgets. Cloning the entire
 * frame with the 1 MiB release-JSON helper would incorrectly spend the payload
 * budget on its envelope/events. Inspect the root without invoking getters,
 * reject unknown keys, then clone each known field through the same strict
 * JSON helper; all component shape/size limits are checked below. */
function snapshotRecord(value: unknown): Record<string, unknown> {
  const path = 'SnapshotEnvelopeV2';
  if (typeof value !== 'object' || value === null || Array.isArray(value)) fail(path, 'must be a JSON object');
  const prototype = Object.getPrototypeOf(value);
  if ((prototype !== Object.prototype && prototype !== null) || Object.getOwnPropertySymbols(value).length > 0) {
    fail(path, 'must be a plain JSON object');
  }
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const root: Record<string, unknown> = {};
  for (const [key, descriptor] of Object.entries(descriptors)) {
    if (!('value' in descriptor) || !descriptor.enumerable) fail(path, 'must contain enumerable data fields');
    Object.defineProperty(root, key, { value: descriptor.value, enumerable: true });
  }
  exactKeys(root, SNAPSHOT_REQUIRED, ['match', 'result'], path);
  return Object.fromEntries(Object.entries(root).map(([key, child]) => [key, cloneJson(child)]));
}

function canonicalObjectByteLength(value: Record<string, unknown>): number {
  const entries = Object.entries(value);
  return 2 + Math.max(0, entries.length - 1) + entries.reduce((bytes, [key, child]) => (
    bytes + utf8ByteLength(JSON.stringify(key)) + 1 + utf8ByteLength(canonicalJson(child))
  ), 0);
}

export function assertValidSnapshotEnvelopeV2<TPayload>(value: unknown, options: { requireTerminalResult?: boolean } = {}): asserts value is SnapshotEnvelopeV2<TPayload> {
  const envelope = snapshotRecord(value);
  if (canonicalObjectByteLength(envelope) > PROTOCOL_V2_LIMITS.snapshotBytes) {
    fail('SnapshotEnvelopeV2', 'exceeds 2 MiB total canonical JSON');
  }
  if (envelope.v !== PROTOCOL_V2) fail('SnapshotEnvelopeV2.v', 'must be 2');
  if (options.requireTerminalResult && envelope.result === undefined) fail('SnapshotEnvelopeV2.result', 'terminal-results negotiation requires result (null before completion)');
  string(envelope.gameId, 'SnapshotEnvelopeV2.gameId', 128, TOKEN);
  string(envelope.matchId, 'SnapshotEnvelopeV2.matchId', 128, TOKEN);
  int(envelope.revision, 'SnapshotEnvelopeV2.revision');
  const envelopeTick = int(envelope.tick, 'SnapshotEnvelopeV2.tick');
  if (!PHASES.includes(envelope.phase as MatchPhase)) fail('SnapshotEnvelopeV2.phase', 'is unsupported');
  const playerCount = validatePlayers(envelope.players);
  if (!Array.isArray(envelope.scores) || envelope.scores.length !== playerCount) fail('SnapshotEnvelopeV2.scores', 'must align with players');
  envelope.scores.forEach((score, slot) => finite(score, `SnapshotEnvelopeV2.scores[${slot}]`));
  for (const key of ['winnerSlot', 'pausedBySlot'] as const) {
    const entry = envelope[key];
    if (entry !== null) int(entry, `SnapshotEnvelopeV2.${key}`, 0, playerCount - 1);
  }
  if (envelope.result !== undefined && envelope.result !== null) {
    assertValidMatchResultV2(envelope.result);
    if (envelope.phase !== 'ended') fail('SnapshotEnvelopeV2.result', 'only ended matches may declare a terminal result');
    const result = envelope.result;
    if (result.matchId !== envelope.matchId || result.configDigest !== envelope.configDigest || result.terminalTick > envelopeTick
      || canonicalJson(result.scores) !== canonicalJson(envelope.scores)) fail('SnapshotEnvelopeV2.result', 'must match envelope identity, scores and terminal tick');
    const summary = matchOutcomeSummaryV2(result.outcome);
    if (envelope.winnerSlot !== summary.winnerSlot || envelope.endReason !== summary.endReason) fail('SnapshotEnvelopeV2.result', 'summary fields must match the canonical terminal result');
  } else if (envelope.endReason !== null && !END_REASONS.includes(envelope.endReason as MatchEndReason)) fail('SnapshotEnvelopeV2.endReason', 'requires a supported canonical terminal result');
  if (envelope.phase === 'ended') {
    if (envelope.result === null) fail('SnapshotEnvelopeV2.result', 'ended matches require a terminal result');
    if (envelope.endReason === null) fail('SnapshotEnvelopeV2.endReason', 'ended matches require a terminal reason');
  } else if (envelope.winnerSlot !== null || envelope.endReason !== null) {
    fail('SnapshotEnvelopeV2', 'only ended matches may declare a winner or terminal reason');
  }
  if (envelope.countdownMs !== null && finite(envelope.countdownMs, 'SnapshotEnvelopeV2.countdownMs') < 0) {
    fail('SnapshotEnvelopeV2.countdownMs', 'must be nonnegative');
  }
  if (!Array.isArray(envelope.events) || envelope.events.length > PROTOCOL_V2_LIMITS.snapshotVisibleEvents) fail('SnapshotEnvelopeV2.events', 'must contain at most 256 visible events');
  if (utf8ByteLength(canonicalJson(envelope.events)) > PROTOCOL_V2_LIMITS.snapshotVisibleEventBytes) {
    fail('SnapshotEnvelopeV2.events', 'exceeds 512 KiB visible-event canonical JSON');
  }
  const cursor = int(envelope.eventCursor, 'SnapshotEnvelopeV2.eventCursor');
  let previousSeq = 0;
  envelope.events.forEach((event, index) => {
    assertValidWorldEventV2(event, `SnapshotEnvelopeV2.events[${index}]`);
    if (event.eventSeq <= previousSeq) fail('SnapshotEnvelopeV2.events', 'must be ordered by increasing eventSeq');
    if (event.eventSeq > cursor) fail(`SnapshotEnvelopeV2.events[${index}].eventSeq`, 'cannot exceed eventCursor');
    if (event.tick > envelopeTick) fail(`SnapshotEnvelopeV2.events[${index}].tick`, 'cannot exceed envelope tick');
    for (const key of ['actorSlot', 'targetSlot'] as const) {
      const slot = event[key];
      if (slot !== undefined && slot >= playerCount) fail(`SnapshotEnvelopeV2.events[${index}].${key}`, 'must reference an existing player slot');
    }
    if (event.audience.kind === 'slots' && event.audience.slots.some((slot) => slot >= playerCount)) {
      fail(`SnapshotEnvelopeV2.events[${index}].audience.slots`, 'must reference existing player slots');
    }
    if (event.audience.kind === 'teams' && event.audience.teams.some((team) => team >= playerCount)) {
      fail(`SnapshotEnvelopeV2.events[${index}].audience.teams`, 'must reference a possible canonical team');
    }
    previousSeq = event.eventSeq;
  });
  digest(envelope.configDigest, 'SnapshotEnvelopeV2.configDigest');
  int(envelope.lastCommandResultSeq, 'SnapshotEnvelopeV2.lastCommandResultSeq');
  validateResync(envelope.resync);
  if (envelope.resync !== null && (envelope.resync as { kind: string }).kind === 'full-resync' && envelope.events.length !== 0) {
    fail('SnapshotEnvelopeV2.events', 'must be empty on a full-resync frame');
  }
  if (envelope.match !== undefined) {
    assertValidMatchStateV1(envelope.match, playerCount);
    if (envelope.phase === 'ended' && envelope.match.clock.running) fail('SnapshotEnvelopeV2.match.clock', 'ended matches must stop their authoritative clock');
  }
  if (utf8ByteLength(canonicalJson(envelope.payload)) > PROTOCOL_V2_LIMITS.snapshotPayloadBytes) fail('SnapshotEnvelopeV2.payload', 'exceeds 1 MiB canonical JSON');
}

export function isValidSnapshotEnvelopeV2<TPayload>(value: unknown): value is SnapshotEnvelopeV2<TPayload> {
  try {
    assertValidSnapshotEnvelopeV2<TPayload>(value);
    return true;
  } catch {
    return false;
  }
}

export function assertValidEventCursorAckV2(value: unknown): asserts value is EventCursorAckV2 {
  const ack = record(value, 'EventCursorAckV2');
  exactKeys(ack, ['v', 'type', 'matchId', 'eventCursor'], [], 'EventCursorAckV2');
  assertV2Header(ack, 'eventCursorAck', 'EventCursorAckV2');
  string(ack.matchId, 'EventCursorAckV2.matchId', 128, TOKEN);
  int(ack.eventCursor, 'EventCursorAckV2.eventCursor');
}

export function assertValidResumeRequestV2(value: unknown): asserts value is ResumeRequestV2 {
  const request = record(value, 'ResumeRequestV2');
  exactKeys(
    request,
    ['v', 'type', 'matchId', 'playerId', 'lastRevision', 'eventCursor', 'inputEpoch', 'lastCommandResultSeq'],
    [],
    'ResumeRequestV2',
  );
  assertV2Header(request, 'resumeRequest', 'ResumeRequestV2');
  string(request.matchId, 'ResumeRequestV2.matchId', 128, TOKEN);
  string(request.playerId, 'ResumeRequestV2.playerId', 128, TOKEN);
  int(request.lastRevision, 'ResumeRequestV2.lastRevision');
  int(request.eventCursor, 'ResumeRequestV2.eventCursor');
  int(request.inputEpoch, 'ResumeRequestV2.inputEpoch', 1);
  int(request.lastCommandResultSeq, 'ResumeRequestV2.lastCommandResultSeq');
}

export function assertValidResumeResultV2(value: unknown): asserts value is ResumeResultV2 {
  const result = record(value, 'ResumeResultV2');
  assertV2Header(result, 'resumeResult', 'ResumeResultV2');
  string(result.matchId, 'ResumeResultV2.matchId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
  string(result.playerId, 'ResumeResultV2.playerId', PROTOCOL_V2_LIMITS.identityCharacters, TOKEN);
  if (result.status === 'resumed') {
    exactKeys(result, ['v', 'type', 'matchId', 'playerId', 'status', 'inputEpoch', 'eventCursor'], [], 'ResumeResultV2');
    int(result.inputEpoch, 'ResumeResultV2.inputEpoch', 1);
    int(result.eventCursor, 'ResumeResultV2.eventCursor');
  } else if (result.status === 'full-resync') {
    exactKeys(result, ['v', 'type', 'matchId', 'playerId', 'status', 'inputEpoch', 'eventCursor', 'reason'], [], 'ResumeResultV2');
    int(result.inputEpoch, 'ResumeResultV2.inputEpoch', 1);
    int(result.eventCursor, 'ResumeResultV2.eventCursor');
    if (!RESYNC_REASONS.includes(result.reason as (typeof RESYNC_REASONS)[number])) fail('ResumeResultV2.reason', 'is unsupported');
  } else if (result.status === 'rejected') {
    exactKeys(result, ['v', 'type', 'matchId', 'playerId', 'status', 'reason'], [], 'ResumeResultV2');
    if (!RESUME_REJECT_REASONS.includes(result.reason as (typeof RESUME_REJECT_REASONS)[number])) fail('ResumeResultV2.reason', 'is unsupported');
  } else {
    fail('ResumeResultV2.status', 'is unsupported');
  }
}

export function assertValidMatchConnectionState(value: unknown): asserts value is MatchConnectionState {
  const state = record(value, 'MatchConnectionState');
  if (state.state === 'reconnecting') {
    exactKeys(state, ['state', 'attempt', 'deadlineMs'], [], 'MatchConnectionState');
    int(state.attempt, 'MatchConnectionState.attempt', 1, PROTOCOL_V2_LIMITS.reconnectAttempts);
    int(state.deadlineMs, 'MatchConnectionState.deadlineMs');
  } else if (state.state === 'resyncing') {
    exactKeys(state, ['state', 'reason'], [], 'MatchConnectionState');
    if (!RESYNC_REASONS.includes(state.reason as (typeof RESYNC_REASONS)[number])) fail('MatchConnectionState.reason', 'is unsupported');
  } else if (state.state === 'failed') {
    exactKeys(state, ['state', 'reason', 'canRetry'], [], 'MatchConnectionState');
    if (!MATCH_CONNECTION_FAILURE_REASONS_V2.includes(state.reason as (typeof MATCH_CONNECTION_FAILURE_REASONS_V2)[number])) fail('MatchConnectionState.reason', 'is unsupported');
    if (typeof state.canRetry !== 'boolean') fail('MatchConnectionState.canRetry', 'must be boolean');
  } else {
    exactKeys(state, ['state'], [], 'MatchConnectionState');
    if (!MATCH_CONNECTION_STATES.includes(state.state as (typeof MATCH_CONNECTION_STATES)[number])) fail('MatchConnectionState.state', 'is unsupported');
  }
}

export function assertValidMatchReplayMetadataV1(value: unknown): asserts value is MatchReplayMetadataV1 {
  const metadata = record(value, 'MatchReplayMetadataV1');
  exactKeys(
    metadata,
    ['schemaVersion', 'matchId', 'protocolVersion', 'runtimeContractDigest', 'simDigest', 'capabilityDigest', 'configDigest', 'matchConfig'],
    [],
    'MatchReplayMetadataV1',
  );
  if (metadata.schemaVersion !== 1) fail('MatchReplayMetadataV1.schemaVersion', 'must be 1');
  string(metadata.matchId, 'MatchReplayMetadataV1.matchId', 128, TOKEN);
  if (metadata.protocolVersion !== PROTOCOL_VERSION && metadata.protocolVersion !== PROTOCOL_V2) fail('MatchReplayMetadataV1.protocolVersion', 'is unsupported');
  digest(metadata.runtimeContractDigest, 'MatchReplayMetadataV1.runtimeContractDigest');
  digest(metadata.simDigest, 'MatchReplayMetadataV1.simDigest');
  digest(metadata.capabilityDigest, 'MatchReplayMetadataV1.capabilityDigest');
  digest(metadata.configDigest, 'MatchReplayMetadataV1.configDigest');
  assertValidMatchConfigV1(metadata.matchConfig);
  if (metadata.simDigest !== metadata.matchConfig.artifactDigest) fail('MatchReplayMetadataV1.simDigest', 'does not match matchConfig artifactDigest');
  if (metadata.capabilityDigest !== metadata.matchConfig.capabilityDigest) fail('MatchReplayMetadataV1.capabilityDigest', 'does not match matchConfig');
}

/** Recompute the config digest after structural validation. */
export async function verifyMatchReplayMetadataV1(value: unknown): Promise<MatchReplayMetadataV1> {
  // Detach the complete metadata before the digest yields. Validating, hashing,
  // comparing and returning different views of caller-owned data could accept
  // one config identity and return another after an in-flight mutation.
  const metadata = cloneJson(value);
  assertValidMatchReplayMetadataV1(metadata);
  const expected = await canonicalDigest(metadata.matchConfig);
  if (expected !== metadata.configDigest) fail('MatchReplayMetadataV1.configDigest', 'does not match matchConfig');
  return deepFreeze(metadata);
}

export function isValidV2Message(value: unknown): boolean {
  try {
    if (typeof value !== 'object' || value === null) return false;
    // Read only a data discriminator before delegating to a strict validator.
    // Cloning here would impose the control-message 1 MiB JSON cap on a valid
    // snapshot's independently bounded payload and events.
    const descriptor = Object.getOwnPropertyDescriptor(value, 'type');
    const type: unknown = descriptor && 'value' in descriptor ? descriptor.value : undefined;
    if (type === undefined) assertValidSnapshotEnvelopeV2(value);
    else if (type === 'clientHello') assertValidClientHelloV2(value);
    else if (type === 'seatNegotiation') assertValidSeatNegotiationV2(value);
    else if (type === 'serverHello') assertValidServerHelloV2(value);
    else if (type === 'protocolReject') assertValidProtocolRejectV2(value);
    else if (type === 'command') assertValidCommandEnvelopeV2(value);
    else if (type === 'commandResult') assertValidCommandResultV2(value);
    else if (type === 'eventCursorAck') assertValidEventCursorAckV2(value);
    else if (type === 'resumeRequest') assertValidResumeRequestV2(value);
    else if (type === 'resumeResult') assertValidResumeResultV2(value);
    else if (type === 'matchResult') assertValidMatchResultV2(value);
    else fail('ProtocolV2Message.type', 'is unsupported');
    return true;
  } catch {
    return false;
  }
}

/** Type-only assertion that the supported protocol union remains intentional. */
const _supported: readonly SupportedProtocolVersion[] = [PROTOCOL_VERSION, PROTOCOL_V2];
void _supported;
